// `useListEditor` — collapses the immutable add / remove-at / update-at
// boilerplate copy-pasted across the array sub-editors (BusinessEditor's
// socials + pricing products, PersonaEditor, SentenceListEditor).
//
// It is a thin binding over a controlled `values` + `onChange(next)` pair: it
// owns no state and calls no React hooks, so it produces the same fresh
// closures the inline handlers did (behaviour-identical). `updateAt` takes an
// updater fn so the same helper serves object lists (merge a patch:
// `(x) => ({ ...x, ...patch })`) and scalar lists (replace: `() => value`).

export interface ListEditor<T> {
  /** Append `item` to the end of the list. */
  add: (item: T) => void;
  /** Remove the item at `index`. */
  removeAt: (index: number) => void;
  /** Replace the item at `index` with `update(currentItem)`. */
  updateAt: (index: number, update: (item: T) => T) => void;
}

export function useListEditor<T>(values: T[], onChange: (next: T[]) => void): ListEditor<T> {
  return {
    add: (item) => onChange([...values, item]),
    removeAt: (index) => onChange(values.filter((_, i) => i !== index)),
    updateAt: (index, update) =>
      onChange(values.map((item, i) => (i === index ? update(item) : item))),
  };
}
